import { strictBillSchema } from './canonical';
import { CatalogGroup, catalogImport } from './catalog';
import { platformTopicFault, saveDestination, TenantDestination } from './destinations';
import type { QaCheck, QaSettings } from './qa';
import { DirectoryTenantPick, importDirectory } from './directory-import';
import {
  addCredential,
  AuthScheme,
  authSchemeFault,
  removeAuthScheme,
  saveAuthScheme,
  setExternalUnitId,
  duplicateBusiness,
  removeVendorRule,
  renameBusiness,
  revokeCredential,
  saveVendorRule,
  VendorRule,
} from './identity';
import { keepDocsAddress } from './ingress';
import { visibleGroups } from './overview';
import { schemaDocument } from './schema';
import {
  ConsoleState,
  grantVendorGroups,
  clearVendorSetup,
  shareGroupWithShops,
  templates,
} from './store';
import { DirectorySource, FieldSource, KEPT_SECRET, OriginalSource } from './types';
import { CanonicalSchema, RouteDef, RouteIngress, Sink, Vendor, VendorIngressDefaults } from './types';

export type AssetChange =
  | { kind: 'failure-alerts'; enabled: boolean; webhookUrl?: string }
  | { kind: 'docs-source'; urls: string[] }
  | { kind: 'default-destination'; topic: string }
  | { kind: 'qa-settings'; id: string; settings: QaSettings }
  | { kind: 'qa-checks'; id: string; checks: QaCheck[] }
  | { kind: 'directory-source'; source: DirectorySource }
  | { kind: 'remove-directory-source' }
  | { kind: 'field-source'; source: FieldSource }
  | { kind: 'remove-field-source' }
  | { kind: 'original-source'; source: OriginalSource }
  | { kind: 'remove-original-source' }
  | {
      /** Route groups read out of an API's own documentation, with their public addresses. */
      kind: 'catalog-import';
      groups: CatalogGroup[];
    }
  | {
      /** Tenants chosen out of the directory, copied in so the door can be answered from memory. */
      kind: 'directory-import';
      vendorId?: string;
      tenants: DirectoryTenantPick[];
    }
  | { kind: 'tenant-groups'; vendorId: string; tenantId: string; groupIds: string[] }
  | { kind: 'business-groups'; vendorId: string; businessId: string; groupIds: string[] }
  | { kind: 'tenant-owned-groups'; vendorId: string; tenantId: string; groupIds: string[] }
  | { kind: 'tenant-ingress'; id: string; tenantId: string; ingress: Partial<RouteIngress> | null }
  | { kind: 'vendor-order'; id: string; beforeId: string }
  | { kind: 'restore-delivery'; id: string; version: number }
  | { kind: 'tenant-destination'; id: string; tenantId: string; destination: TenantDestination['destination'] | null }
  | { kind: 'strict-bill-schema'; id: string }
  | {
      /** How one endpoint behaves: whether it maps at all, and what a payload must carry. */
      kind: 'endpoint-rules';
      id: string;
      passThrough?: boolean;
      isBatch?: boolean;
      /** How the vendor sends its bills; XML is read as JSON at the door. */
      format?: 'json' | 'xml';
      requiredFields?: string[];
      /** A JSON pointer to the field a vendor would recognise, such as `/billId`. */
      referenceField?: string;
      /** JSON pointers to fields that are never written down, such as `/customerMobile`. */
      redactFields?: string[];
      /** The Casa service that answers this endpoint; empty sends it back to the queue. */
      upstream?: string;
    }
  | { kind: 'sample-expectation'; id: string; expectation: 'pass' | 'reject'; expectedViolationPath?: string }
  | { kind: 'canary'; id: string; sampleId: string }
  | { kind: 'schema'; value: CanonicalSchema }
  | {
      kind: 'vendor';
      id: string;
      name: string;
      archived?: boolean;
      slug?: string;
      pos?: string;
      /** The address every endpoint of this vendor starts from. */
      ingressDefaults?: VendorIngressDefaults;
    }
  | { kind: 'remove-schema'; id: string }
  | { kind: 'group-sign-in'; id: string; authSchemeId?: string }
  | { kind: 'group-strict'; id: string; strict: boolean }
  | { kind: 'group-for-all-shops'; id: string }
  | { kind: 'clear-vendor-setup'; id: string }
  | { kind: 'grant-vendor-groups'; id: string; tenantId?: string }
  | { kind: 'rename-business'; id: string; name: string }
  | { kind: 'external-unit-id'; businessId: string; externalUnitId: string }
  | { kind: 'reserved-prefixes'; prefixes: string[] }
  | { kind: 'duplicate-business'; id: string; name: string }
  | {
      kind: 'credential';
      businessId?: string;
      tenantId?: string;
      label: string;
      keyHash: string;
      vendorId?: string;
      allowedRouteIds?: string[];
    }
  | { kind: 'revoke-credential'; id: string }
  | { kind: 'auth-scheme'; scheme: AuthScheme }
  | { kind: 'remove-auth-scheme'; id: string }
  | { kind: 'vendor-rule'; rule: VendorRule }
  | { kind: 'remove-vendor-rule'; id: string };

const object = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('A configuration object is required.');
  return value as Record<string, unknown>;
};

const tenantDetails = (value: unknown): Record<string, string> => {
  const details = object(value);
  return Object.fromEntries(
    Object.entries(details).map(([name, held]) => {
      if (!/^[A-Za-z][A-Za-z0-9_.-]{0,63}$/.test(name)) throw new Error(`"${name}" is not a usable detail name.`);
      if (/^(api_?key|auth_?key|token|secret|password)$/i.test(name))
        throw new Error(`"${name}" looks like a secret and is not kept.`);
      if (typeof held !== 'string' || held.length > 200)
        throw new Error(`The tenant's ${name} must be text up to 200 characters.`);
      return [name, held];
    }),
  );
};

const text = (value: unknown, label: string): string => {
  if (typeof value !== 'string' || !value.trim() || value.length > 500)
    throw new Error(`${label} is required (up to 500 characters).`);
  return value.trim();
};

/*
 * Upper case is allowed because an id is not always ours to choose. A tenant picked out of the
 * directory keeps the id its owner gave it, exactly as written — lowercasing it here would make
 * a second identifier for the same tenant, which is the one thing this must never do.
 */
const idOf = (value: unknown): string => {
  const id = text(value, 'Id');
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(id))
    throw new Error('Use letters, numbers, dots, underscores or hyphens for the id.');
  return id;
};

const versionOf = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1)
    throw new Error('Version must be a positive integer.');
  return value;
};

const unique = (values: string[], label: string): void => {
  if (new Set(values).size !== values.length) throw new Error(`${label} must be unique.`);
};

export const applyAssetChange = (state: ConsoleState, input: unknown): ConsoleState => {
  const change = object(input);
  if (change.kind === 'docs-source') {
    if (!Array.isArray(change.urls)) throw new Error('Give the docs addresses as a list.');
    const urls = [...new Set(change.urls.map((url) => String(url).trim()).filter(Boolean))];
    if (urls.length === 0 || urls.length > 5) throw new Error('Give one to five docs addresses.');
    if (urls.some((url) => !/^https?:\/\/\S+$/.test(url))) throw new Error('A docs address starts with http:// or https://.');
    return { ...state, docsSource: { urls } };
  }
  if (change.kind === 'directory-source') {
    const source = object(change.source) as unknown as DirectorySource;
    if (source.method !== 'GET' && source.method !== 'POST') throw new Error('Use GET or POST.');
    if (typeof source.url !== 'string' || !/^https?:\/\/[^\s]+$/.test(source.url.trim()))
      throw new Error('A directory source needs an http or https address.');

    /*
     * A header value the browser never saw comes back as the marker, and saving the marker would
     * replace a live token with eight bullets. It means "keep what you have" instead.
     */
    const held = state.directorySource?.headers ?? {};
    const headers = Object.fromEntries(
      Object.entries(source.headers ?? {}).map(([name, value]) => [
        name,
        value === KEPT_SECRET ? (held[name] ?? '') : value,
      ]),
    );

    return { ...state, directorySource: { ...source, headers, url: source.url.trim() } };
  }
  if (change.kind === 'field-source') {
    const source = object(change.source) as unknown as FieldSource;
    if (source.method !== 'GET' && source.method !== 'POST') throw new Error('Use GET or POST.');
    if (typeof source.url !== 'string' || !/^https?:\/\/[^\s]+$/.test(source.url.trim()))
      throw new Error('A field source needs an http or https address.');

    /* The same held-secret rule as the directory: eight bullets mean "keep what you have". */
    const kept = state.fieldSource?.headers ?? {};
    const headers = Object.fromEntries(
      Object.entries(source.headers ?? {}).map(([name, value]) => [
        name,
        value === KEPT_SECRET ? (kept[name] ?? '') : value,
      ]),
    );

    return { ...state, fieldSource: { ...source, headers, url: source.url.trim() } };
  }
  if (change.kind === 'original-source') {
    const source = object(change.source) as unknown as OriginalSource;
    if (source.method !== 'GET' && source.method !== 'POST') throw new Error('Use GET or POST.');
    if (typeof source.url !== 'string' || !/^https?:\/\/[^\s]+$/.test(source.url.trim()))
      throw new Error('A record of delivered bills needs an http or https address.');
    if (!source.url.includes('{{billId}}') && !(source.body ?? '').includes('{{billId}}'))
      throw new Error('Write {{billId}} where the bill being reversed belongs.');

    const kept = state.originalSource?.headers ?? {};
    const headers = Object.fromEntries(
      Object.entries(source.headers ?? {}).map(([name, value]) => [
        name,
        value === KEPT_SECRET ? (kept[name] ?? '') : value,
      ]),
    );

    return { ...state, originalSource: { ...source, headers, url: source.url.trim() } };
  }
  if (change.kind === 'remove-original-source') {
    const { originalSource: _gone, ...rest } = state;

    return rest as ConsoleState;
  }
  if (change.kind === 'remove-field-source') {
    const { fieldSource: _dropped, ...left } = state;

    return left as ConsoleState;
  }
  if (change.kind === 'remove-directory-source') {
    const { directorySource: _removed, ...rest } = state;

    return rest as ConsoleState;
  }
  if (change.kind === 'directory-import') {
    const vendorId = change.vendorId === undefined ? undefined : idOf(change.vendorId);
    if (vendorId && !state.vendors.some((vendor) => vendor.id === vendorId)) throw new Error('Vendor not found.');
    if (!Array.isArray(change.tenants) || change.tenants.length === 0)
      throw new Error('Choose at least one tenant to bring in.');
    if (change.tenants.length > 500) throw new Error('Bring in at most 500 tenants at a time.');

    const picks: DirectoryTenantPick[] = change.tenants.map((raw) => {
      const tenant = object(raw);
      const businesses = tenant.businesses === undefined ? [] : tenant.businesses;
      if (!Array.isArray(businesses)) throw new Error('A tenant\u2019s businesses must be a list.');
      const tenantKeyHash =
        tenant.apiKeyHash === undefined ? undefined : text(tenant.apiKeyHash, 'Key hash').toLowerCase();
      if (tenantKeyHash !== undefined && !/^[a-f0-9]{64}$/.test(tenantKeyHash))
        throw new Error('A key hash must be 64 hex characters.');

      return {
        id: idOf(tenant.id),
        name: text(tenant.name, 'Tenant name'),
        apiKeyHash: tenantKeyHash,
        ...(tenant.details === undefined ? {} : { details: tenantDetails(tenant.details) }),
        businesses: businesses.map((entry) => {
          const business = object(entry);
          const keyHash =
            business.apiKeyHash === undefined ? undefined : text(business.apiKeyHash, 'Key hash').toLowerCase();
          if (keyHash !== undefined && !/^[a-f0-9]{64}$/.test(keyHash))
            throw new Error('A key hash must be 64 hex characters.');

          return { id: idOf(business.id), name: text(business.name, 'Business name'), apiKeyHash: keyHash };
        }),
      };
    });
    unique(
      picks.map((pick) => pick.id),
      'Tenant ids',
    );

    const next = importDirectory(state, picks, vendorId);
    if (next === state) throw new Error('Nothing in that selection could be brought in.');
    return next;
  }
  if (change.kind === 'catalog-import') {
    if (!Array.isArray(change.groups) || change.groups.length === 0)
      throw new Error('Choose at least one group to bring in.');
    if (change.groups.length > 100) throw new Error('Bring in at most 100 groups at a time.');

    const groups: CatalogGroup[] = change.groups.map((raw) => {
      const group = object(raw);
      const endpoints = Array.isArray(group.endpoints) ? group.endpoints : [];
      if (endpoints.length === 0) throw new Error(`Group "${String(group.name)}" has no endpoints.`);
      if (endpoints.length > 200) throw new Error('A group may bring in at most 200 endpoints.');

      return {
        id: idOf(group.id),
        name: text(group.name, 'Group name'),
        description: typeof group.description === 'string' ? group.description : undefined,
        endpoints: endpoints.map((entry) => {
          const endpoint = object(entry);
          const method = String(endpoint.method ?? '').toUpperCase();
          if (method !== 'GET' && method !== 'POST' && method !== 'PUT') throw new Error('Use GET, POST or PUT.');
          const path = text(endpoint.path, 'Endpoint path');
          if (!path.startsWith('/')) throw new Error('An endpoint path starts with /.');

          return {
            name: text(endpoint.name, 'Endpoint name'),
            slug: idOf(endpoint.slug),
            method: method as 'GET' | 'POST' | 'PUT',
            path,
            description: typeof endpoint.description === 'string' ? endpoint.description : undefined,
            body: endpoint.body === undefined ? undefined : object(endpoint.body),
            examples: Array.isArray(endpoint.examples)
              ? endpoint.examples.slice(0, 20).map((raw) => {
                  const example = object(raw);
                  return {
                    name: text(example.name, 'Example name').slice(0, 200),
                    payload: typeof example.payload === 'string' ? example.payload.slice(0, 200_000) : '',
                  };
                }).filter((example) => example.payload)
              : undefined,
            isBatch: endpoint.isBatch === true || undefined,
            auth: Array.isArray(endpoint.auth)
              ? endpoint.auth.slice(0, 10).map((header) => String(header).slice(0, 100))
              : undefined,
          };
        }),
      };
    });

    const brought = catalogImport(groups);
    /* A sign-in already here may have been corrected by hand, such as its refusal codes; keep it. */
    const signIns = brought.authSchemes.filter((scheme) => !state.authSchemes?.some((held) => held.id === scheme.id));
    /*
     * Reading the documentation again is how a change to it is picked up, so a group that is
     * already here is replaced rather than refused — and a schema keeps its id, because routes
     * already point at it.
     */
    const heldTemplates = templates(state).filter(
      (template) => !brought.templates.some((fresh) => fresh.id === template.id),
    );
    const heldSchemas = state.schemas.filter((schema) => !brought.schemas.some((fresh) => fresh.id === schema.id));

    return {
      ...state,
      templates: [...heldTemplates, ...brought.templates],
      schemas: [...heldSchemas, ...brought.schemas],
      authSchemes: [...(state.authSchemes ?? []), ...signIns],
    };
  }
  if (change.kind === 'endpoint-rules') {
    const routeId = idOf(change.id);
    const route = state.routes.find((item) => item.id === routeId);
    if (!route) throw new Error('Endpoint not found.');

    const schema = state.schemas.find((item) => item.id === route.canonicalSchemaId);
    const known = new Set(schema?.fields.map((field) => field.path) ?? []);
    const requiredFields =
      change.requiredFields === undefined
        ? route.requiredFields
        : (() => {
            if (!Array.isArray(change.requiredFields)) throw new Error('Required fields must be a list.');
            const paths = change.requiredFields.map((path) => text(path, 'Field path'));
            /* A path that is not in the schema would be a rule nothing could ever satisfy. */
            const unknown = paths.filter((path) => known.size > 0 && !known.has(path));
            if (unknown.length > 0) throw new Error(`Not fields of this endpoint: ${unknown.join(', ')}.`);
            return [...new Set(paths)];
          })();

    /*
     * A pointer, not a field name. `billId` and `/billId` both mean the same thing to whoever
     * types it, so both are accepted and stored the one way — otherwise half the endpoints on a
     * platform end up configured with a pointer that quietly matches nothing.
     */
    const pointer = (value: unknown, what: string): string => {
      const said = text(value as string, what).trim();
      if (said.includes(' ')) throw new Error(`${what} must be a JSON pointer, such as /billId.`);

      return said.startsWith('/') ? said : `/${said}`;
    };

    const referenceField =
      change.referenceField === undefined
        ? route.referenceField
        : change.referenceField === ''
          ? undefined
          : pointer(change.referenceField, 'Reference field');

    const redactFields =
      change.redactFields === undefined
        ? route.redactFields
        : (() => {
            if (!Array.isArray(change.redactFields)) throw new Error('Redacted fields must be a list.');
            const paths = change.redactFields.map((path) => pointer(path, 'Redacted field'));

            return [...new Set(paths)];
          })();

    const upstream =
      change.upstream === undefined
        ? route.upstream
        : change.upstream === ''
          ? undefined
          : (() => {
              const name = text(change.upstream, 'Service').trim();
              if (!/^[a-z][a-z0-9-]*$/.test(name)) {
                throw new Error('A service name uses lower-case letters, digits and -, such as rule-server.');
              }
              return name;
            })();

    const next: RouteDef = {
      ...route,
      upstream,
      passThrough: change.passThrough === undefined ? route.passThrough : change.passThrough === true,
      isBatch: change.isBatch === undefined ? route.isBatch : change.isBatch === true,
      format: change.format === undefined ? route.format : change.format === 'xml' ? 'xml' : undefined,
      requiredFields: requiredFields?.length ? requiredFields : undefined,
      referenceField,
      redactFields: redactFields?.length ? redactFields : undefined,
    };

    return { ...state, routes: state.routes.map((item) => (item.id === routeId ? next : item)) };
  }
  if (change.kind === 'qa-settings') {
    const id = idOf(change.id);
    if (!state.routes.some((route) => route.id === id)) throw new Error('Endpoint not found.');
    const given = object(change.settings);
    const tenantId = String(given.tenantId ?? '').trim();
    const businessId = String(given.businessId ?? '').trim();
    const business = state.businesses?.find((item) => item.id === businessId);
    if (!state.tenants.some((tenant) => tenant.id === tenantId)) throw new Error('Choose a shop Casa knows.');
    if (!business || business.tenantId !== tenantId) throw new Error('Choose a business of that shop.');
    const values = Object.entries(object(given.values ?? {})).map(([name, value]) => [
      name.trim(),
      String(value).trim(),
    ]);
    if (values.length > 30) throw new Error('Give at most 30 QA values.');
    if (values.some(([name, value]) => !/^[A-Za-z_][\w-]{0,63}$/.test(name) || !value || value.length > 200))
      throw new Error('A QA value needs a field name (letters, digits, _ or -) and a value.');
    const skip = Array.isArray(given.skip) ? [...new Set(given.skip.map(String))].slice(0, 200) : [];
    const qa: QaSettings = {
      tenantId,
      businessId,
      values: Object.fromEntries(values),
      ...(skip.length ? { skip } : {}),
    };
    return { ...state, routes: state.routes.map((route) => (route.id === id ? { ...route, qa } : route)) };
  }
  if (change.kind === 'qa-checks') {
    const id = idOf(change.id);
    if (!state.routes.some((route) => route.id === id)) throw new Error('Endpoint not found.');
    if (!Array.isArray(change.checks) || change.checks.length > 10) throw new Error('Give at most 10 checks.');
    const checks: QaCheck[] = change.checks.map((raw) => {
      const check = object(raw);
      const target = state.routes.find((route) => route.id === check.routeId);
      if (!target?.upstream) throw new Error('A check reads an endpoint that Casa\'s backend answers.');
      const field = String(check.field ?? '').trim();
      if (!field) throw new Error('Say which field of the answer is checked.');
      if (check.expect !== 'exists' && check.expect !== 'equals' && check.expect !== 'increases')
        throw new Error('A check expects the field to exist, to equal a value, or to go up.');
      return {
        routeId: target.id,
        field,
        expect: check.expect,
        ...(check.expect === 'equals' ? { value: String(check.value ?? '') } : {}),
      };
    });
    return {
      ...state,
      routes: state.routes.map((route) => {
        if (route.id !== id) return route;
        const { qaChecks: _previous, ...rest } = route;
        return checks.length ? { ...rest, qaChecks: checks } : rest;
      }),
    };
  }
  if (change.kind === 'default-destination') {
    const topic = typeof change.topic === 'string' ? change.topic.trim() : '';
    if (!/^[A-Za-z0-9._-]{1,249}$/.test(topic))
      throw new Error('A Kafka topic is letters, digits, dots, dashes or underscores.');
    const sink: Sink = { kind: 'kafka', target: topic };
    const fault = platformTopicFault(sink);
    if (fault) throw new Error(fault);

    return { ...state, defaultDestination: sink };
  }
  if (change.kind === 'failure-alerts') {
    const enabled = change.enabled === true;
    const raw = typeof change.webhookUrl === 'string' ? change.webhookUrl.trim() : '';
    if (enabled && !/^https:\/\/[^\s]+$/.test(raw)) throw new Error('Failure alerts need an https webhook address.');

    return { ...state, alerts: { ...(state.alerts ?? {}), failures: { enabled, webhookUrl: raw || undefined } } };
  }
  if (change.kind === 'tenant-groups') {
    const vendorId = idOf(change.vendorId);
    const tenantId = idOf(change.tenantId);
    const vendor = state.vendors.find((item) => item.id === vendorId);
    if (!vendor?.tenantIds.includes(tenantId)) throw new Error('Connect the tenant to this vendor first.');
    if (
      !Array.isArray(change.groupIds) ||
      change.groupIds.some(
        (id) => typeof id !== 'string' || !state.groups.some((group) => group.id === id && group.vendorId === vendorId),
      )
    )
      throw new Error('Choose only route groups configured for this vendor.');
    const groupIds = [...new Set(change.groupIds as string[])];
    return {
      ...state,
      tenantGroups: [
        ...(state.tenantGroups ?? []).filter((item) => item.vendorId !== vendorId || item.tenantId !== tenantId),
        { vendorId, tenantId, groupIds },
      ],
    };
  }
  if (change.kind === 'business-groups') {
    const vendorId = idOf(change.vendorId);
    const businessId = idOf(change.businessId);
    const business = (state.businesses ?? []).find((item) => item.id === businessId);
    if (!business) throw new Error('No such business.');
    const vendor = state.vendors.find((item) => item.id === vendorId);
    if (!vendor?.tenantIds.includes(business.tenantId))
      throw new Error('Connect this business tenant to the vendor first.');
    const offered = new Set(visibleGroups(state, vendorId, `tenant:${business.tenantId}`).map((group) => group.id));
    if (!Array.isArray(change.groupIds) || change.groupIds.some((id) => typeof id !== 'string' || !offered.has(id)))
      throw new Error('Choose only route groups this business tenant holds.');
    const groupIds = [...new Set(change.groupIds as string[])];

    return {
      ...state,
      businessGroups: [
        ...(state.businessGroups ?? []).filter((item) => item.vendorId !== vendorId || item.businessId !== businessId),
        { vendorId, businessId, groupIds },
      ],
    };
  }
  if (change.kind === 'tenant-owned-groups') {
    const vendorId = idOf(change.vendorId);
    const tenantId = idOf(change.tenantId);
    const vendor = state.vendors.find((item) => item.id === vendorId);
    if (!vendor?.tenantIds.includes(tenantId)) throw new Error('Connect the tenant to this vendor first.');
    const offered = new Set(visibleGroups(state, vendorId, `tenant:${tenantId}`).map((group) => group.id));
    if (!Array.isArray(change.groupIds) || change.groupIds.some((id) => typeof id !== 'string' || !offered.has(id)))
      throw new Error('Choose only route groups this tenant holds.');
    const groupIds = [...new Set(change.groupIds as string[])];

    return {
      ...state,
      tenantOwnedGroups: [
        ...(state.tenantOwnedGroups ?? []).filter((item) => item.vendorId !== vendorId || item.tenantId !== tenantId),
        { vendorId, tenantId, groupIds },
      ],
    };
  }
  if (change.kind === 'tenant-ingress') {
    const route = state.routes.find((item) => item.id === change.id);
    if (
      !route ||
      typeof change.tenantId !== 'string' ||
      !state.vendors.find((item) => item.id === route.vendorId)?.tenantIds.includes(change.tenantId)
    )
      throw new Error('Connect this tenant to the route vendor first.');
    const ingress =
      change.ingress === null ? null : keepDocsAddress(state, route, object(change.ingress) as Partial<RouteIngress>);
    return {
      ...state,
      tenantIngress: [
        ...(state.tenantIngress ?? []).filter((item) => item.routeId !== route.id || item.tenantId !== change.tenantId),
        ...(ingress ? [{ routeId: route.id, tenantId: change.tenantId, ingress: structuredClone(ingress) }] : []),
      ],
    };
  }
  if (change.kind === 'vendor-order') {
    const vendor = state.vendors.find((item) => item.id === change.id);
    if (!vendor || !state.vendors.some((item) => item.id === change.beforeId)) throw new Error('Vendor not found.');
    if (change.id === change.beforeId) return state;
    const vendors = state.vendors.filter((item) => item.id !== vendor.id);
    vendors.splice(
      vendors.findIndex((item) => item.id === change.beforeId),
      0,
      vendor,
    );
    return { ...state, vendors };
  }
  if (change.kind === 'restore-delivery') {
    const delivery = state.deliveryRevisions?.find(
      (item) => item.routeId === change.id && item.version === change.version,
    );
    if (!delivery) throw new Error('Delivery revision not found.');
    if (
      !state.revisions.some(
        (item) => item.routeId === delivery.routeId && item.contentHash === delivery.transformationHash,
      )
    )
      throw new Error('The referenced transformation is unavailable.');
    return {
      ...state,
      deliveryRevisions: state.deliveryRevisions?.map((item) =>
        item.routeId === delivery.routeId ? { ...item, live: item.version === delivery.version } : item,
      ),
      revisions: state.revisions.map((item) =>
        item.routeId === delivery.routeId && item.scope === 'shared'
          ? { ...item, live: item.contentHash === delivery.transformationHash }
          : item,
      ),
    };
  }
  if (change.kind === 'tenant-destination') {
    const destination = change.destination === null ? null : object(change.destination);
    const fault = platformTopicFault(destination);
    if (fault) throw new Error(fault);
    return saveDestination(
      state,
      idOf(change.id),
      idOf(change.tenantId),
      destination as TenantDestination['destination'] | null,
    );
  }
  if (change.kind === 'strict-bill-schema') {
    const route = state.routes.find((item) => item.id === change.id);
    if (!route) throw new Error('Route not found.');
    const previous = state.schemas.find((item) => item.id === route.canonicalSchemaId);
    if (!previous || previous.code !== 'bill') throw new Error('Select a canonical bill schema first.');
    const schema = strictBillSchema(previous);
    // Upgrading an already-strict route would add another copy of its template to the library.
    if (previous.id === schema.id) return state;
    const originalTemplate = templates(state).find((item) =>
      item.members.some((member) => member.canonicalSchemaId === previous.id),
    );
    const upgradedTemplate = originalTemplate
      ? {
          ...originalTemplate,
          id: `${originalTemplate.id}-strict-v4`,
          version: originalTemplate.version + 1,
          members: originalTemplate.members.map((member) =>
            member.canonicalSchemaId === previous.id ? { ...member, canonicalSchemaId: schema.id } : member,
          ),
        }
      : undefined;
    return {
      ...state,
      templates:
        upgradedTemplate && !templates(state).some((item) => item.id === upgradedTemplate.id)
          ? [...templates(state), upgradedTemplate]
          : templates(state),
      schemas: state.schemas.some((item) => item.id === schema.id) ? state.schemas : [...state.schemas, schema],
      routes: state.routes.map((item) => (item.id === route.id ? { ...item, canonicalSchemaId: schema.id } : item)),
      runs: state.runs.filter((item) => item.routeId !== route.id),
    };
  }
  if (change.kind === 'sample-expectation') {
    const sample = state.samples.find((item) => item.id === change.id);
    if (!sample) throw new Error('Sample not found.');
    if (change.expectation !== 'pass' && change.expectation !== 'reject') throw new Error('Choose pass or reject.');
    if (change.expectation === 'reject' && state.routes.some((route) => route.canarySampleId === sample.id))
      throw new Error('Choose a positive canary before marking this sample as an expected rejection.');
    return {
      ...state,
      samples: state.samples.map((item) =>
        item.id === sample.id
          ? {
              ...item,
              expectation: change.expectation as 'pass' | 'reject',
              expectedViolationPath:
                typeof change.expectedViolationPath === 'string' ? change.expectedViolationPath : undefined,
            }
          : item,
      ),
      runs: state.runs.filter((item) => item.routeId !== sample.routeId),
    };
  }
  if (change.kind === 'canary') {
    const route = state.routes.find((item) => item.id === change.id);
    const sample = state.samples.find(
      (item) => item.id === change.sampleId && item.routeId === route?.id && item.scope === 'shared',
    );
    if (!route || !sample || sample.expectation === 'reject')
      throw new Error('Choose a positive shared sample as the canary.');
    return {
      ...state,
      routes: state.routes.map((item) => (item.id === route.id ? { ...item, canarySampleId: sample.id } : item)),
      runs: state.runs.filter((item) => item.routeId !== route.id),
    };
  }

  if (change.kind === 'schema') {
    const raw = object(change.value);
    if (!Array.isArray(raw.fields) || (raw.fields.length === 0 && !raw.jsonSchema))
      throw new Error('Add schema fields or import a JSON Schema.');
    const fields = raw.fields.map((entry) => {
      const field = object(entry);
      const type = text(field.type, 'Field type');
      if (!['string', 'number', 'integer', 'boolean', 'object', 'array', 'any'].includes(type))
        throw new Error(`Unsupported field type: ${type}.`);
      const path = text(field.path, 'Field path');
      if (
        path
          .split('.')
          .some((part) => !part || ['__proto__', 'prototype', 'constructor'].includes(part.replace(/\[\]$/, '')))
      )
        throw new Error('Invalid or reserved field path.');
      return { path, type, required: field.required === true };
    });
    unique(
      fields.map((field) => field.path),
      'Field paths',
    );
    const value: CanonicalSchema = {
      id: idOf(raw.id),
      code: idOf(raw.code),
      title: text(raw.title, 'Schema title'),
      version: versionOf(raw.version),
      fields,
    };
    if (raw.jsonSchema !== undefined) value.jsonSchema = object(raw.jsonSchema);
    schemaDocument(value);
    if (state.schemas.some((schema) => schema.id === value.id))
      throw new Error(
        'A schema with this id already exists. Create a new version with a new id to preserve existing routes.',
      );
    return { ...state, schemas: [...state.schemas, value] };
  }
  if (change.kind === 'credential') {
    // the key itself is generated in the browser and never sent; only its hash arrives here
    const keyHash = text(change.keyHash, 'Key hash');
    if (!/^[a-f0-9]{64}$/.test(keyHash)) throw new Error('A key hash must be 64 hex characters.');
    const next = addCredential(state, {
      keyHash,
      businessId: change.businessId ? idOf(change.businessId) : '',
      tenantId: change.tenantId === undefined ? undefined : idOf(change.tenantId),
      label: text(change.label, 'Label'),
      vendorId: change.vendorId === undefined ? undefined : idOf(change.vendorId),
      allowedRouteIds: Array.isArray(change.allowedRouteIds) ? change.allowedRouteIds.map(idOf) : undefined,
    });
    if (next === state) {
      throw new Error('That business or tenant is unknown, or the key is already registered.');
    }
    return next;
  }
  if (change.kind === 'revoke-credential') {
    if (!state.credentials?.some((credential) => credential.id === change.id)) throw new Error('Key not found.');
    return revokeCredential(state, String(change.id));
  }
  if (change.kind === 'reserved-prefixes') {
    if (!Array.isArray(change.prefixes)) throw new Error('Send the reserved paths as a list.');
    const prefixes = [
      ...new Set(
        change.prefixes
          .map((prefix) =>
            String(prefix)
              .trim()
              .replace(/^\/+|\/+$/g, ''),
          )
          .filter(Boolean),
      ),
    ];
    const bad = prefixes.find((prefix) => !/^[A-Za-z0-9._~-]+(\/[A-Za-z0-9._~-]+)*$/.test(prefix));
    if (bad) throw new Error(`"${bad}" is not a plain path. Use letters, digits, - or _, with no {placeholder}.`);
    return { ...state, reservedPrefixes: prefixes };
  }
  if (change.kind === 'external-unit-id') {
    const businessId = idOf(change.businessId);
    if (!state.businesses?.some((business) => business.id === businessId)) throw new Error('Business not found.');
    const external = String(change.externalUnitId ?? '').trim();
    if (external.length > 120) throw new Error('That id is too long (up to 120 characters).');
    const clash = state.businesses.find(
      (business) => business.id !== businessId && external && business.externalUnitId === external,
    );
    if (clash) throw new Error(`${clash.name} already answers to ${external}.`);
    return setExternalUnitId(state, businessId, external);
  }
  if (change.kind === 'auth-scheme') {
    const scheme = object(change.scheme) as unknown as AuthScheme;
    const fault = authSchemeFault({ ...scheme, id: idOf(scheme.id) }, state);
    if (fault) throw new Error(fault);
    return saveAuthScheme(state, { ...scheme, id: idOf(scheme.id) });
  }
  if (change.kind === 'remove-auth-scheme') {
    if (!state.authSchemes?.some((scheme) => scheme.id === change.id)) throw new Error('Scheme not found.');
    const group = state.groups.find((item) => item.authSchemeId === change.id);
    if (group) throw new Error(`The route group ${group.name} still signs in this way. Change it first.`);
    const template = templates(state).find(
      (item) => item.authSchemeId === change.id || item.members.some((member) => member.authSchemeId === change.id),
    );
    if (template) throw new Error(`The template ${template.name} still signs in this way. Change it first.`);
    const next = removeAuthScheme(state, String(change.id));
    if (next === state) throw new Error('A public path still uses this scheme. Point it elsewhere first.');
    return next;
  }
  if (change.kind === 'vendor-rule') {
    const rule = object(change.rule) as unknown as VendorRule;
    if (!state.routes.some((route) => route.id === rule.routeId)) throw new Error('Route not found.');
    if (!state.vendors.some((vendor) => vendor.id === rule.vendorId)) throw new Error('Vendor not found.');
    if (!Array.isArray(rule.clauses)) throw new Error('A rule needs a list of clauses.');
    return saveVendorRule(state, { ...rule, id: rule.id || `vr-${rule.routeId}-${rule.vendorId}` });
  }
  if (change.kind === 'remove-vendor-rule') {
    if (!state.vendorRules?.some((rule) => rule.id === change.id)) throw new Error('Rule not found.');
    return removeVendorRule(state, String(change.id));
  }
  /*
   * Everything below this line reads `change.id`, so an unrecognised kind has to be named here.
   * Letting one fall through reports "Id is required" for a change that never had an id — which
   * is how a control plane older than the console disguises itself as a validation bug.
   */
  const NAMED_BY_ID = new Set([
    'vendor',
    'remove-schema',
    'group-sign-in',
    'group-strict',
    'group-for-all-shops',
    'clear-vendor-setup',
    'grant-vendor-groups',
    'rename-business',
    'duplicate-business',
  ]);
  if (typeof change.kind !== 'string' || !NAMED_BY_ID.has(change.kind))
    throw new Error(
      `Unknown configuration change "${String(change.kind)}". This control plane may be older than the console.`,
    );
  const id = idOf(change.id);
  if (change.kind === 'vendor') {
    if (!state.vendors.some((vendor) => vendor.id === id)) throw new Error('Vendor not found.');
    const name = text(change.name, 'Vendor name');
    if (state.vendors.some((vendor) => vendor.id !== id && vendor.name.toLowerCase() === name.toLowerCase()))
      throw new Error('Another vendor has this name.');
    const patch: Partial<Vendor> = { name };
    if (change.ingressDefaults !== undefined) {
      patch.ingressDefaults = object(change.ingressDefaults) as VendorIngressDefaults;
      const schemeId = patch.ingressDefaults.authSchemeId;
      if (schemeId && !state.authSchemes?.some((scheme) => scheme.id === schemeId))
        throw new Error('Authentication scheme not found.');
    }
    if (change.slug !== undefined) {
      const nextSlug = idOf(change.slug);
      const vendor = state.vendors.find((item) => item.id === id)!;
      if (nextSlug !== (vendor.slug ?? vendor.id)) {
        if (
          state.revisions.some((revision) =>
            state.routes.some((route) => route.id === revision.routeId && route.vendorId === id),
          )
        )
          throw new Error('Published endpoint URLs cannot be retired by this runtime; change the display name only.');
        if (state.vendors.some((item) => item.id !== id && (item.slug ?? item.id) === nextSlug))
          throw new Error('Another vendor uses this endpoint slug.');
        patch.slug = nextSlug;
      }
    }
    if (typeof change.archived === 'boolean') patch.archived = change.archived;
    if (change.pos !== undefined) patch.pos = text(change.pos, 'POS name');
    return { ...state, vendors: state.vendors.map((vendor) => (vendor.id === id ? { ...vendor, ...patch } : vendor)) };
  }
  if (change.kind === 'remove-schema') {
    if (
      state.routes.some((route) => route.canonicalSchemaId === id) ||
      templates(state).some((template) => template.members.some((member) => member.canonicalSchemaId === id))
    )
      throw new Error('This schema is used by a route or template. Keep it for those integrations.');
    return { ...state, schemas: state.schemas.filter((schema) => schema.id !== id) };
  }
  if (change.kind === 'grant-vendor-groups') {
    if (!state.vendors.some((vendor) => vendor.id === id)) throw new Error('Vendor not found.');
    return grantVendorGroups(state, id, change.tenantId ? idOf(change.tenantId) : undefined, true);
  }
  if (change.kind === 'clear-vendor-setup') {
    if (!state.vendors.some((vendor) => vendor.id === id)) throw new Error('Vendor not found.');
    return clearVendorSetup(state, id);
  }
  if (change.kind === 'group-for-all-shops') {
    if (!state.groups.some((group) => group.id === id)) throw new Error('Route group not found.');
    return shareGroupWithShops(state, id);
  }
  /* A vendor that already added the group keeps the copy it took; only groups added later change. */
  if (change.kind === 'group-strict') {
    if (!state.groups.some((group) => group.id === id)) throw new Error('Route group not found.');
    return {
      ...state,
      groups: state.groups.map((group) => {
        if (group.id !== id) return group;
        const { strict: _previous, ...rest } = group;
        return change.strict === true ? { ...rest, strict: true } : rest;
      }),
    };
  }
  if (change.kind === 'group-sign-in') {
    if (!state.groups.some((group) => group.id === id)) throw new Error('Route group not found.');
    const schemeId = change.authSchemeId ? String(change.authSchemeId) : undefined;
    if (schemeId && !state.authSchemes?.some((scheme) => scheme.id === schemeId))
      throw new Error('Authentication scheme not found.');
    return {
      ...state,
      groups: state.groups.map((group) => {
        if (group.id !== id) return group;
        const { authSchemeId: _previous, ...rest } = group;
        return schemeId ? { ...rest, authSchemeId: schemeId } : rest;
      }),
    };
  }
  if (change.kind === 'rename-business')
    return renameBusiness(state, idOf(change.id), text(change.name, 'Business name'));
  if (change.kind === 'duplicate-business')
    return duplicateBusiness(state, idOf(change.id), text(change.name, 'Business name'));

  throw new Error('Unknown configuration change.');
};
